'use client';
import React from 'react';
import ReactECharts from 'echarts-for-react';
import { HoldingsQuote } from '@benzinga/quotes-v3-holdings-manager';
import {
  HorizontalRuleModule,
  ProTickerHover,
  SendLinkContext,
  SparkLineModule,
  TickerHeaderModule,
  Sparkline,
  useAutoCompleteSymbols,
  TickerNotesModule,
} from '@benzinga/pro-ui';
import styled from '@benzinga/themetron';
import { VisualizationArgs, getColor, getMinMax, formatNumberWithSuffix, HQ } from '@benzinga/visualization-utils';
import { HeatMapArgs, HeatMapProps, TooltipProps, TooltipValuesProps } from './entity';
import { StockSymbol } from '@benzinga/session';
import { camelCaseToTitleCase, isNilOrEmpty } from '@benzinga/utils';
import ReactDOM from 'react-dom';
import { useConfig } from './hooks/useConfig';
import { useBatchQuotes } from '@benzinga/quotes-v3-manager-hooks';
import { Quote } from '@benzinga/quotes-v3-manager';
import { getDefaultLabel, getGroupCategory } from './helpers';
import { RefreshInterval } from '@benzinga/scanner-config-manager';

const TooltipValues: React.FC<TooltipValuesProps> = React.memo(props => {
  return (
    <TooltipDataWrapper>
      {`${camelCaseToTitleCase(props.quoteProperty)} : ${formatNumberWithSuffix(props.value, props.quoteProperty)}`}
    </TooltipDataWrapper>
  );
});

const useFields = (traces: HeatMapArgs[], isSubscribed = true) => {
  const groupCategory = getGroupCategory(traces[0].groupBy);
  return React.useMemo(() => {
    if (!isSubscribed) {
      return new Set<HQ>(['symbol', 'changePercent']);
    }
    const requiredFields = new Set<HQ>([
      'symbol',
      traces[0].color,
      traces[0].display,
      'gicsSectorName',
      'gicsIndustryName',
    ] as HQ[]);
    groupCategory.forEach(category => {
      requiredFields.add(category as HQ);
    });
    return requiredFields;
  }, [groupCategory, isSubscribed, traces]);
};

const TooltipModules: React.FC<TooltipProps & HeatMapArgs & { quotes: HoldingsQuote[] }> = React.memo(props => {
  const sendLink = React.useContext(SendLinkContext);

  const currentQuote = React.useMemo(() => props.quotes.find(q => q.symbol === props.name), [props.quotes, props.name]);

  if (!currentQuote) return null;

  const displayValue = currentQuote[props.display];
  const colorValue = currentQuote[props.color];

  if (displayValue === undefined || colorValue === undefined) return null;

  return (
    <>
      <TickerHeaderModule onSymbolClick={sendLink.onSymbolClick} />
      <HorizontalRuleModule />
      <SparkLineModule />
      <HorizontalRuleModule />
      <TooltipValues quoteProperty={props.display} value={displayValue} />
      <TooltipValues quoteProperty={props.color} value={colorValue} />
      <TickerNotesModule />
    </>
  );
});

const GroupTooltip: React.FC<TooltipProps & HeatMapArgs & { quotes: HoldingsQuote[] }> = React.memo(props => {
  const children = React.useMemo(() => props.children?.slice(0, 10), [props.children]);
  const isCombinedCategory =
    props.groupBy === 'combined' && Array.isArray(props.children) && !('symbol' in props.children[0]);

  if (!props.quotes?.length || isCombinedCategory) return null;

  const QuoteDataRenderer: React.FC<{ display: string; color: string; symbol: StockSymbol }> = React.memo(
    ({ color, display, symbol }) => {
      const quote = React.useMemo(() => props.quotes.find(quote => quote.symbol === symbol), [symbol]);

      if (!quote) return null;

      const displayValue = quote[display];
      const colorValue = quote[color];

      if (displayValue === undefined || colorValue === undefined) return null;

      return (
        <QuoteDataWrapper>
          <DisplayText>{formatNumberWithSuffix(displayValue, display)}</DisplayText>
          <ColorText color={colorValue}>{formatNumberWithSuffix(colorValue, color)}</ColorText>
        </QuoteDataWrapper>
      );
    },
  );

  return (
    <ProTickerHover
      modules={
        <GroupTooltipWrapper>
          <GroupTitle>{props.name}</GroupTitle>
          <QuoteConfig>
            <Label>Value</Label>
            <Label>Color</Label>
          </QuoteConfig>
          {children?.map(child => (
            <ChildWrapper key={child.name}>
              <ChildName>{child.name}</ChildName>
              <SparklineWrapper>
                <Sparkline height="compact" omitTextInfo symbol={child.name} />
              </SparklineWrapper>
              <QuoteDataWrapper>
                <QuoteDataRenderer color={props.color} display={props.display} symbol={child.name} />
              </QuoteDataWrapper>
            </ChildWrapper>
          ))}
        </GroupTooltipWrapper>
      }
      symbol={props.name}
    />
  );
});
const Tooltip: React.FC<TooltipProps & HeatMapArgs & { quotes }> = React.memo(props => {
  if (props.children && props.children.length > 0) {
    return <GroupTooltip {...props} />;
  }
  return <ProTickerHover modules={<TooltipModules {...props} />} symbol={props.name} />;
});
export const HeatMap: React.FC<VisualizationArgs<HeatMapProps>> = props => {
  const fields = useFields(props.args.traces, true);
  const symbols = useAutoCompleteSymbols(props.sources);
  const {
    color: traceColor,
    display: traceDisplay,
    groupBy: traceGroupBy,
  } = React.useMemo(() => props.args.traces[0], [props.args.traces]);
  const quotes = useBatchQuotes(symbols ?? [], fields as Set<keyof Quote>, {
    refreshInterval: RefreshInterval['1m'],
  });
  const sendLink = React.useContext(SendLinkContext);
  const echartsRef = React.useRef<ReactECharts>(null);

  //storing quote data for heatmap
  const groupedQuotes = React.useMemo(() => {
    if (isNilOrEmpty(quotes) || isNilOrEmpty(props.args.traces[0])) return [];
    return quotes.map(quote => ({
      symbol: quote.symbol,
      [traceColor]: isNaN(quote[traceColor]) ? 0 : quote[traceColor],
      [traceDisplay]: isNaN(quote[traceDisplay]) ? 0 : quote[traceDisplay],
      ...getGroupCategory(traceGroupBy).reduce((acc, group) => {
        const groupValue = quote[group];
        if (groupValue) {
          acc[group] = groupValue;
        }
        return acc;
      }, {}),
    }));
  }, [props.args.traces, quotes, traceColor, traceDisplay, traceGroupBy]);

  const buildCellData = React.useCallback(
    (groupQuotes: HoldingsQuote[]) => {
      let colorMax = -Infinity;
      let colorMin = Infinity;
      const { max, min } = getMinMax(groupQuotes ?? [], traceColor);
      return (groupQuotes ?? []).map(groupQuote => {
        const colorValue = groupQuote[traceColor];
        colorMin = Math.min(colorMin, isNaN(colorValue) ? 0 : colorValue);
        colorMax = Math.max(colorMax, isNaN(colorValue) ? 0 : colorValue);
        const color = isNilOrEmpty(getColor(groupQuote, traceColor, min, max))
          ? '#ccc'
          : getColor(groupQuote, traceColor, min, max);
        //symbol level
        return {
          depth: 1,
          id: groupQuote.symbol,
          itemStyle: { color: color },
          name: groupQuote.symbol,
          symbol: groupQuote.symbol,
          value: [Math.abs(groupQuote[traceDisplay]) ?? 0, colorValue],
        };
      });
    },
    [traceColor, traceDisplay],
  );

  const buildHeatmapData = React.useCallback(
    (groupQuotes: HoldingsQuote[]) => {
      let colorMax = -Infinity;
      let colorMin = Infinity;
      const industryKey =
        traceGroupBy === 'combined'
          ? getGroupCategory('industry')[0] || `Other Industry`
          : getGroupCategory(traceGroupBy)[0] || getDefaultLabel(traceGroupBy);

      if (isNilOrEmpty(groupQuotes)) return;

      const groupQuoteDataByIndustry = groupQuotes.reduce((acc, quote) => {
        const key = quote[industryKey] || getDefaultLabel(traceGroupBy);
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(quote);
        return acc;
      }, {});

      const data = Object.entries(groupQuoteDataByIndustry).map(([key, quotes]) => {
        const children = buildCellData(quotes as HoldingsQuote[]);
        return {
          children,
          id: key,
          name: key,
          value: children.reduce(
            (acc, child) => {
              acc[0] += isNaN(child.value[0]) ? 0 : child.value[0];
              acc[1] += isNaN(child.value[1]) ? 0 : child.value[1];
              colorMax = Math.max(colorMax, isNaN(child.value[1]) ? 0 : child.value[1]);
              colorMin = Math.min(colorMin, isNaN(child.value[1]) ? 0 : child.value[1]);
              return acc;
            },
            [0, 0],
          ),
        };
      });

      return { data, max: colorMax, min: colorMin };
    },
    [buildCellData, traceGroupBy],
  );

  const data = React.useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    //building data for combined mode
    if (traceGroupBy === 'combined') {
      const groupQuotesBySector = groupedQuotes.reduce((acc, quote) => {
        const sector = quote[getGroupCategory('sector')[0]] || 'Other Sector';

        if (!acc[sector]) {
          acc[sector] = [];
        }

        acc[sector].push(quote);
        return acc;
      }, {});

      const data = Object.entries(groupQuotesBySector).map(([sector, groupQuotes]) => {
        const localData = buildHeatmapData(groupQuotes as HoldingsQuote[]);
        const updatedData = localData?.data.flatMap(item => {
          max = Math.max(max, item?.value[1] ?? -Infinity);
          min = Math.min(min, item?.value[1] ?? Infinity);
          return item;
        });
        return {
          children: updatedData,
          id: sector,
          name: sector,
          value: updatedData?.flatMap(item => item.value) ?? [0, 0],
        };
      });

      return { data: data, max, min };
    }
    //building data for sector/industry mode
    const groupedData = groupedQuotes.reduce((acc, quote) => {
      const groupKey = quote[getGroupCategory(traceGroupBy)[0]] || getDefaultLabel(traceGroupBy);

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }

      acc[groupKey].push(quote);
      return acc;
    }, {});

    const data = Object.entries(groupedData).map(([_groupKey, groupQuotes]) =>
      buildHeatmapData(groupQuotes as HoldingsQuote[]),
    );
    //formatting data for echarts

    const updatedData = data.flatMap(item => {
      max = Math.max(max, item?.max ?? -Infinity);
      min = Math.min(min, item?.min ?? Infinity);
      return item?.data;
    });

    return { data: updatedData, max, min };
  }, [buildHeatmapData, groupedQuotes, traceGroupBy]);

  const onEvents = React.useMemo(
    () => ({
      click: event => {
        const isValidTicker = /^[A-Z]+$/.test(event.name);
        if (isValidTicker) {
          sendLink.onSymbolClick(event.name);
        } else if (echartsRef.current) {
          const chartInstance = echartsRef.current.getEchartsInstance();
          chartInstance.dispatchAction({
            targetNodeId: event.data.id,
            type: 'treemapRootToNode',
          });
        }
      },
    }),
    [sendLink],
  );
  const { options, tooltip, tooltipHost } = useConfig(props.args, symbols, data, echartsRef);

  const emptyState = props.args.emptyState;
  const noDataMessage =
    emptyState?.noDataMessage !== undefined
      ? emptyState.noDataMessage
      : 'No data to display for the value selected. Please select another value';

  return data.data.length > 0 ? (
    <>
      {tooltipHost &&
        tooltip.name &&
        ReactDOM.createPortal(
          <TooltipContainer>
            <Tooltip
              {...tooltip}
              color={traceColor}
              display={traceDisplay}
              groupBy={traceGroupBy}
              key={tooltip.name}
              quotes={quotes}
            />
          </TooltipContainer>,
          tooltipHost,
        )}
      <div style={{ height: '100%', width: '100% !important' }}>
        <ReactECharts
          key={symbols?.join(',')}
          onEvents={onEvents}
          option={options}
          ref={echartsRef}
          style={{ height: '100%', width: '100% !important' }}
        />
      </div>
    </>
  ) : noDataMessage === '' ? null : (
    <Text>{noDataMessage}</Text>
  );
};

const TooltipContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
`;

const Text = styled.div`
  text-align: center;
  align-items: center;
  padding: 100px;
  margin: auto;
  font-size: 30px;
`;

const TooltipDataWrapper = styled.div`
  padding: 6px;
  display: flex;
  font-weight: 500;
  gap: 6px;
  font-size: 14px;
  font-family: 'Nimbus Sans', helvetica, arial, sans-serif;
`;
const QuoteConfig = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
  padding-right: 10px;
  font-weight: bold;
`;

const Label = styled.div`
  width: 60px;
  text-align: right;
  margin-left: 20px;
`;

const QuoteDataWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 140px;
`;

const DisplayText = styled.div`
  width: 60px;
  text-align: right;
`;

const ColorText = styled.span<{ color: number }>`
  width: 60px;
  text-align: right;
  margin-left: 20px;
  color: ${props => (props.color < 0 ? props.theme.colors.statistic.negative : props.theme.colors.statistic.positive)};
`;

const ChildWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  font-size: 14px;
  min-width: 340px;
  max-width: max-content;
  div {
    border-bottom: none !important;
  }
`;

const ChildName = styled.div`
  flex: 0 0 50px;
  text-align: left;
`;

const SparklineWrapper = styled.div`
  width: 45%;
  margin: 0 10px;
`;

const GroupTitle = styled.div`
  padding: 10px;
  font-size: 16px;
  font-weight: 500;
  font-style: italic;
`;

const GroupTooltipWrapper = styled.div`
  border-radius: 5px;
`;
